import { timesheetWorkedMilliseconds, timesheetIsOnLunch } from '../../../../shared/timesheet-work-time';
export type JobContact = {
  id: string;
  name: string;
  phone: string | null;
  phoneType: "mobile" | "home" | "work" | "other" | null;
  email: string | null;
  memo: string | null;
  doNotAlert: boolean;
  isPrimary: boolean;
};

export const emailKey = (value: string | null) => (value ?? "").trim().toLowerCase();
export const phoneKey = (value: string | null) => {
  const digits = (value ?? "").replace(/\D/g, "");
  return digits.length === 11 && digits.startsWith("1") ? digits.slice(1) : digits;
};

export function groupJobContacts(contacts: JobContact[]) {
  const people = new Map<string, { name: string; records: JobContact[]; phones: JobContact[]; emails: JobContact[]; memos: string[] }>();
  for (const contact of [...contacts].sort((a, b) => Number(b.isPrimary) - Number(a.isPrimary))) {
    const name = contact.name.trim();
    const key = name.toLowerCase().replace(/\s+/g, " ") || emailKey(contact.email) || phoneKey(contact.phone) || contact.id;
    const person = people.get(key) ?? { name: name || "Contact", records: [], phones: [], emails: [], memos: [] };
    person.records.push(contact);
    if (phoneKey(contact.phone) && !person.phones.some((entry) => phoneKey(entry.phone) === phoneKey(contact.phone))) person.phones.push(contact);
    if (emailKey(contact.email) && !person.emails.some((entry) => emailKey(entry.email) === emailKey(contact.email))) person.emails.push(contact);
    const memo = contact.memo?.trim();
    if (memo && !person.memos.includes(memo)) person.memos.push(memo);
    people.set(key, person);
  }
  return [...people.values()];
}

export function identicalContacts(a: JobContact, b: JobContact) {
  return a.name.trim().toLowerCase() === b.name.trim().toLowerCase()
    && phoneKey(a.phone) === phoneKey(b.phone) && emailKey(a.email) === emailKey(b.email)
    && a.phoneType === b.phoneType && a.memo === b.memo
    && a.isPrimary === b.isPrimary && a.doNotAlert === b.doNotAlert;
}

export type JobTimeEntry = { id: string; appointmentId?: string | null; userId?: string; technicianName: string; clockIn: string; clockOut: string | null; lunchBreaksJson?: string | null; minutes?: number };
export function summarizeJobTime(entries: JobTimeEntry[], now: number) {
  const people = new Map<string, { id: string; name: string; minutes: number; running: boolean; entries: JobTimeEntry[] }>();
  for (const entry of entries) {
    const id = entry.userId || entry.technicianName;
    const person = people.get(id) ?? { id, name: entry.technicianName || "Unnamed technician", minutes: 0, running: false, entries: [] };
    const elapsed = timesheetWorkedMilliseconds(entry, now) / 60000;
    const minutes = entry.clockOut && entry.minutes !== undefined ? entry.minutes : elapsed;
    person.minutes += Number.isFinite(minutes) ? Math.max(0, Math.floor(minutes)) : 0;
    person.running ||= entry.clockOut === null && !timesheetIsOnLunch(entry);
    person.entries.push(entry);
    people.set(id, person);
  }
  for (const person of people.values()) person.entries.sort((a, b) => Date.parse(b.clockIn) - Date.parse(a.clockIn));
  return [...people.values()].sort((a, b) => Number(b.running) - Number(a.running) || a.name.localeCompare(b.name));
}

export type JobEstimate = {
  id: string; estimateNumber: number; displayNumber?: string | null; title?: string | null;
  summary?: string | null; optionLabels?: string | null; approvedOptionLabels?: string | null; approvedTotalCents?: number | null; originatedFromSoldWork?: number; optionsManaged?: number; status: string; officeStatus?: string;
  totalCents: number; createdAt: string;
  soldWork?: Array<{ optionId: string; optionLabel: string; jobId: string; jobNumber: string; invoiceId: string; invoiceDisplayNumber: string; fulfillment: "now" | "later" }>;
};
export type EstimateDelivery = {
  id: string; kind?: "approval" | "pdf"; recipientEmail: string; sentAt: string; viewedAt: string | null;
  acceptedAt: string | null; revokedAt: string | null; expiresAt: string | null; deliveryError: string | null;
  sentByUserId?: string | null; sentByName?: string | null;
};
export const canEmailEstimate = (estimate: JobEstimate) => !["void", "cancelled", "canceled"].includes(estimate.status);
export function latestDelivery(history: EstimateDelivery[]) {
  return [...history].sort((a, b) => Date.parse(b.sentAt) - Date.parse(a.sentAt))[0];
}
export function deliveryLabel(delivery: EstimateDelivery | undefined, estimate: JobEstimate) {
  if (!delivery) return estimate.status === "draft" ? "Not sent" : "Not emailed";
  if (delivery.deliveryError) return "Delivery failed";
  if (delivery.acceptedAt) return "Approved";
  if (delivery.revokedAt) return "Revoked";
  if (delivery.viewedAt) return "Viewed";
  return delivery.kind === "pdf" ? "PDF sent" : "Sent";
}
export function deliverySender(delivery: EstimateDelivery | undefined) {
  return delivery?.sentByName?.trim() || null;
}

type TimedVisit = { technicianNames?: string | null; id: string; status?: string; scheduledStart: string; scheduledEnd: string; dispatchedAt?: string | null; arrivedAt?: string | null; departedAt?: string | null };
/** Match overlapping time to assigned technicians when available; never duplicate an entry. */
export function groupTimeByVisit<T extends JobTimeEntry & { appointmentId?: string | null }>(visits: TimedVisit[], entries: T[], now = Date.now()) {
  const byVisit = new Map<string, T[]>(visits.map((visit) => [visit.id, []]));
  const unmatched: T[] = [];
  for (const entry of entries) {
    const start = Date.parse(entry.clockIn);
    const end = entry.clockOut ? Date.parse(entry.clockOut) : now;
    let matches = entry.appointmentId ? visits.filter((visit) => visit.id === entry.appointmentId) : visits.filter((visit) => {
      if (["cancelled", "canceled"].includes(visit.status ?? "")) return false;
      const visitStart = Date.parse(visit.dispatchedAt || visit.arrivedAt || visit.scheduledStart);
      const visitEnd = Date.parse(visit.departedAt || visit.scheduledEnd);
      return Number.isFinite(start) && Number.isFinite(end) && end > start && visitEnd > visitStart && start < visitEnd && end > visitStart;
    });
    if (!entry.appointmentId && matches.some((visit) => visit.technicianNames?.trim())) {
      const normalize = (name: string) => name.trim().replace(/\s+/g, " ").toLowerCase();
      const technician = normalize(entry.technicianName);
      // The existing job response exposes assignment names, not assignment IDs.
      // Exact names can disambiguate overlaps; multiple matches remain unresolved.
      matches = matches.filter((visit) => technician && visit.technicianNames?.split(",").some((name) => normalize(name) === technician));
    }
    if (matches.length === 1) byVisit.get(matches[0].id)!.push(entry);
    else unmatched.push(entry);
  }
  return { byVisit, unmatched };
}
